import { FC } from "react";
import { twMerge } from "tailwind-merge";

import { getStudentIdFromAccessId, isStudentAccessId } from "../../../../shared/access-id";
import { PROFILE_PROPERTIES, getUserFullName, getUserInitials } from "../../../../utils/user-utils";

import { SUBMISSION_ID_QUERY_PARAM } from "components/constants";
import { NewSubmissionBadge } from "components/materials/page/assessment/NewSubmissionBadge";
import { ResponseType } from "components/materials/page/responses";
import { t } from "i18n/i18n";
import { InitialsAvatar } from "mds/components/InitialsAvatar";
import { SquareButton, SquareButtonProps } from "mds/components/buttons/SquareButton";
import {
  PersonGroupIcon,
  PersonSingleIcon,
  ReactionTransparentVoteYesIcon,
  ReactionVoteYesIcon,
} from "mds/icons";
import {
  selectSortedSubmissionsByAccessId,
  submissionSelectors,
  userSelectors,
} from "store/selectors";
import { useListSelector, useObjectSelector } from "store/store-hooks";
import { useQueryParam } from "utils/routing/hooks/use-query-params";

export const StudentUserAvatar = ({
  userId,
  isSelected,
  anonymize,
  className,
}: {
  userId: string;
  isSelected?: boolean;
  anonymize?: boolean;
  className?: string;
}) => {
  const user = useObjectSelector((s) => userSelectors.selectById(s, userId), PROFILE_PROPERTIES);

  if (!anonymize) {
    return (
      <InitialsAvatar
        className={className}
        initials={getUserInitials(user)}
        isSelected={isSelected}
      />
    );
  }

  return (
    <div
      className={twMerge(
        "flex size-3 items-center justify-center rounded-full p-3 text-[9px]",
        isSelected ? "text-white" : "text-blue",
        !isSelected && "bg-blue-tint-93",
        className,
      )}
    >
      <PersonSingleIcon />
    </div>
  );
};

const GroupAvatar = ({ isSelected }: { isSelected?: boolean }) => {
  return (
    <div
      className={twMerge(
        "flex size-3 items-center justify-center rounded-full p-3 text-[9px]",
        !isSelected && "bg-hibiscus-tint-95",
      )}
    >
      <PersonGroupIcon className={isSelected ? "text-white" : "text-hibiscus"} />
    </div>
  );
};

const AssessmentItemIcon = ({
  accessId,
  isSelected,
  isAssessedResponse,
  anonymize,
}: {
  accessId: string;
  isSelected?: boolean;
  isAssessedResponse?: boolean;
  anonymize?: boolean;
}) => {
  if (isAssessedResponse) {
    return (
      <div className="flex min-h-6 min-w-6 items-center justify-center">
        <ReactionVoteYesIcon />
      </div>
    );
  }

  if (isStudentAccessId(accessId)) {
    return (
      <StudentUserAvatar
        anonymize={anonymize}
        isSelected={isSelected}
        userId={getStudentIdFromAccessId(accessId)}
      />
    );
  }

  return <GroupAvatar isSelected={isSelected} />;
};

interface AssessmentStudentQueueItemProps {
  response: ResponseType;
  isSelected: boolean;
  anonymize: boolean;
  isAssessedResponse: boolean;
  isSaved: boolean;
  pageId: string;
  onResponseClick: (response: ResponseType) => void;
}

export const AssessmentStudentQueueItem: FC<AssessmentStudentQueueItemProps> = ({
  response,
  isSelected,
  anonymize,
  isAssessedResponse,
  isSaved,
  pageId,
  onResponseClick,
}) => {
  const submissionId = useQueryParam(SUBMISSION_ID_QUERY_PARAM);
  const currentSubmission = useObjectSelector(
    (s) => submissionSelectors.selectById(s, submissionId),
    ["id"],
  );
  const studentUserId = isStudentAccessId(response.id)
    ? getStudentIdFromAccessId(response.id)
    : null;
  const user = useObjectSelector(
    (s) => (studentUserId ? userSelectors.selectById(s, studentUserId) : null),
    PROFILE_PROPERTIES,
  );

  const title = studentUserId && !anonymize && user ? getUserFullName(user) : response.title;

  const studentSubmissions = useListSelector(
    (s) => selectSortedSubmissionsByAccessId(s, pageId, response.id),
    ["created_at", "seen_after_assessed_at", "official_at"],
  );

  const submitBtnAttrs: Omit<SquareButtonProps, "kind" | "size"> = currentSubmission
    ? {
        form: "assessment-grading-form",
        type: "submit",
        name: "submitSource",
        value: `queue:${response.id}`,
      }
    : { onClick: () => onResponseClick(response) };

  return (
    <SquareButton
      aria-label={title}
      aria-selected={isSelected || undefined}
      className={`my-1 w-full justify-start ${isSaved ? "cursor-default border-green bg-green hover:border-green hover:bg-green" : ""}`}
      eventName="Assessment - Student Queue"
      eventProps={{
        pageId,
        userId: response.id,
        submissionId: currentSubmission?.id,
      }}
      kind={isSelected ? "accent" : "ghost"}
      role="option"
      size="m"
      tabIndex={isSelected ? 0 : -1}
      title={title}
      {...submitBtnAttrs}
    >
      {isSaved ? (
        <span className="animate-save-fade flex items-center gap-2">
          <span className="flex min-h-6 min-w-6 items-center justify-center">
            <ReactionTransparentVoteYesIcon className="text-white" />
          </span>
          <div className="min-w-0 text-sm font-medium text-white">{t("assessment.saved")}</div>
        </span>
      ) : (
        <>
          <AssessmentItemIcon
            accessId={response.id}
            anonymize={anonymize}
            isAssessedResponse={isAssessedResponse}
            isSelected={isSelected}
          />
          <div
            className={twMerge(
              "min-w-0 truncate text-sm font-medium",
              isSelected ? "text-white" : "text-black-tint-20",
            )}
          >
            {title}
          </div>
          <NewSubmissionBadge sortedSubmissions={studentSubmissions} />
        </>
      )}
    </SquareButton>
  );
};
